import { DEVICE_LOCALE, translatorFor } from '../messages';
import type { UiLocale } from '../messages';

/**
 * Calendar arithmetic in the tenant's timezone.
 *
 * This is **not** a second scheduling engine and must never become one. Nothing
 * here decides what is free, what overlaps, or when a service ends —
 * `packages/scheduling` is the only implementation of any of that (dev.md rule
 * 3), and the PWA gets those answers from `GET /v1/availability`, which calls
 * it. What lives here is formatting and day arithmetic: which local day an
 * instant falls on, so the mirror can index the book by day, and how to say a
 * time out loud.
 *
 * The distinction is worth policing. "Which day is this instant in Atlantic
 * Standard Time" is a display question. "Is 14:00 free" is a scheduling
 * question. The first can live on the device; the second cannot, because two
 * answers to it is the bug the whole architecture is arranged to prevent.
 *
 * ## The `locale` parameter, and why it is last and optional
 *
 * Every formatter below that produces *words* takes a trailing `locale` that
 * defaults to `DEVICE_LOCALE` — the staff member's language, decided once at
 * module load (`messages/locale.ts`). Trailing and optional because there are
 * around sixty call sites and none of them had anything to say about language;
 * the two that do — a screen rendered under a `<MessagesProvider>` — pass
 * `useLocale()` and get both the sentence and the `Intl` output in one language.
 *
 * The functions that carry catalog words resolve their own `t` from that locale
 * rather than taking a second parameter, for the same reason: a `t` and a locale
 * passed separately are two things that can disagree, and the disagreement would
 * read as "sincronizado hace 4 min" beside a 4:12 PM.
 *
 * **`en-CA` stays where it is.** It appears twice below and neither use is a
 * language: it is the shortest way to get `YYYY-MM-DD` and a zero-padded
 * 24-hour clock out of `Intl` without hand-rolling the padding. Those are
 * *formats* — an index key and a parse target — and translating them would break
 * the mirror.
 */

/** `YYYY-MM-DD` for an instant, in the given IANA zone. */
export function isoDayIn(timezone: string, at: Date | string | number): string {
  const date = at instanceof Date ? at : new Date(at);
  // `en-CA` renders `YYYY-MM-DD`, which is the ISO shape without a hand-rolled
  // pad. `formatToParts` avoids depending on that being true.
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Day arithmetic on the `YYYY-MM-DD` string itself — no zone, no DST trap. */
export function addDays(day: string, delta: number): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const at = new Date(Date.UTC(y, m - 1, d));
  at.setUTCDate(at.getUTCDate() + delta);
  return at.toISOString().slice(0, 10);
}

export function dayDistance(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}

export function formatDayLong(
  day: string,
  timezone: string,
  locale: UiLocale = DEVICE_LOCALE,
): string {
  const at = new Date(`${day}T12:00:00Z`);
  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(at);
}

/**
 * **`hour12: true` in every language**, which is a decision rather than an
 * oversight.
 *
 * Unforced, `Intl` renders `14:00` for `es` — and `packages/scheduling`'s
 * `summary.ts` and `apps/site/src/ui/time.ts` both force the 12-hour clock for
 * the same reason this does: a door in the USVI says *two o'clock* in either
 * language, and a staff screen reading `14:00` beside a guest confirmation
 * reading `2:00 p. m.` would be the same appointment printed two ways. The
 * *marker* is the locale's — `PM` in English, `p. m.` in Spanish — because that
 * part really is language.
 */
export function formatClock(
  iso: string,
  timezone: string,
  locale: UiLocale = DEVICE_LOCALE,
): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(iso));
}

/**
 * Weekday names, Monday-first, from `Intl` rather than from an array.
 *
 * 2024-01-01 was a Monday, so seven consecutive UTC days from it are the week in
 * `DayHours.weekday` order (spec §4.6, and LoveSpa's `meta.work_hours` before
 * it). A hardcoded English list was what Settings had; a list per language would
 * have been the same mistake twice.
 */
export function weekdayNames(locale: UiLocale = DEVICE_LOCALE): string[] {
  const format = new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' });
  return Array.from({ length: 7 }, (_, index) =>
    format.format(new Date(Date.UTC(2024, 0, 1 + index))),
  );
}

/**
 * "last synced 4:12 PM" — and, past a point, how long ago.
 *
 * Spec §8.1: *"the UI says plainly how stale it is"*. Plainly means a clock
 * time, not a spinner and not "syncing…". An owner who can see that the book
 * they are reading is from twenty minutes ago can decide whether to trust it;
 * an owner watching an indeterminate spinner cannot decide anything.
 */
export function formatStaleness(
  lastSyncAt: string | null,
  nowMs: number,
  timezone: string,
  locale: UiLocale = DEVICE_LOCALE,
): string {
  const t = translatorFor(locale);
  if (lastSyncAt === null) return t('time.neverSynced');
  const at = Date.parse(lastSyncAt);
  if (Number.isNaN(at)) return t('time.neverSynced');
  const ageMs = nowMs - at;
  const clock = formatClock(lastSyncAt, timezone, locale);
  if (ageMs < 60_000) return t('time.syncedJustNow');
  if (ageMs < 3_600_000) {
    const minutes = Math.floor(ageMs / 60_000);
    return t('time.lastSyncedMinutes', { clock, minutes });
  }
  const hours = Math.floor(ageMs / 3_600_000);
  return t('time.lastSyncedHours', { clock, hours });
}

/**
 * "read 4:12 PM" — the online screens' version of the sentence above.
 *
 * Deliberately different words from `formatStaleness`. "Synced" is a claim about
 * a mirror that is kept up to date in the background; the kitchen pass and the
 * dock have no mirror, and what they can honestly say is when they last *asked*.
 * Two screens saying "synced" about two different mechanisms would be one word
 * covering a real difference.
 */
export function formatFreshness(
  fetchedAt: number | null,
  nowMs: number,
  timezone: string,
  locale: UiLocale = DEVICE_LOCALE,
): string {
  const t = translatorFor(locale);
  if (fetchedAt === null) return t('time.notReadYet');
  const ageMs = nowMs - fetchedAt;
  const clock = formatClock(new Date(fetchedAt).toISOString(), timezone, locale);
  if (ageMs < 30_000) return t('time.readJustNow');
  if (ageMs < 3_600_000)
    return t('time.readMinutes', { clock, minutes: Math.floor(ageMs / 60_000) });
  return t('time.readHours', { clock, hours: Math.floor(ageMs / 3_600_000) });
}

export function formatMoney(
  cents: number,
  currency: string,
  locale: UiLocale = DEVICE_LOCALE,
): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
}

/**
 * A menu price the way her card prints it — `$85`, and `$42.50` only when there
 * are cents to show. For chips and summaries a guest is quoted from; a ledger
 * or a till total keeps `formatMoney`'s fixed two places.
 */
export function formatMenuPrice(
  cents: number,
  currency: string,
  locale: UiLocale = DEVICE_LOCALE,
): string {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    ...(whole ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {}),
  }).format(cents / 100);
}

/* ------------------------------------------------------------------ */
/* Wall clock ↔ instant, in the tenant's zone                          */
/* ------------------------------------------------------------------ */

/**
 * `<input type="datetime-local">` speaks a wall clock with no zone attached, and
 * the book is kept in the tenant's. These two functions are the conversion, and
 * they are here rather than in the form because "2 PM at the spa" has to mean
 * one instant no matter which device typed it.
 *
 * On an iPad in the shop the tenant zone and the device zone agree and this does
 * nothing visible. On a laptop in another timezone — which is how it gets
 * tested, and how an owner on holiday uses it — it is the difference between a
 * booking at two o'clock and a booking an hour off, which is the exact failure
 * `IsoDateTime`'s "explicit offset, always" rule exists to prevent on the wire.
 */
export function isoToZonedWallClock(iso: string, timezone: string): string {
  if (iso === '') return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const p = zonedParts(timezone, at.getTime());
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

export function zonedWallClockToIso(wallClock: string, timezone: string): string {
  if (wallClock === '') return '';
  const asIfUtc = Date.parse(`${wallClock}:00Z`);
  if (Number.isNaN(asIfUtc)) return '';

  // The zone's offset is itself a function of the instant (DST), so: guess the
  // instant using the offset at the naive reading, then re-read the offset at
  // the guess. One correction settles every case except the hour that does not
  // exist in spring, where the guess lands just outside the gap — which is the
  // same answer any reasonable calendar gives and is not ours to invent.
  const first = zoneOffsetMs(timezone, asIfUtc);
  let instant = asIfUtc - first;
  const second = zoneOffsetMs(timezone, instant);
  if (second !== first) instant = asIfUtc - second;
  return new Date(instant).toISOString();
}

function zoneOffsetMs(timezone: string, atMs: number): number {
  const p = zonedParts(timezone, atMs);
  const asIfUtc = Date.parse(`${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}Z`);
  return asIfUtc - atMs;
}

interface ZonedParts {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
}

function zonedParts(timezone: string, atMs: number): ZonedParts {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(atMs));
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '00';
  // `hour12: false` renders midnight as `24` in some ICU versions.
  const hour = get('hour') === '24' ? '00' : get('hour');
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour,
    minute: get('minute'),
    second: get('second'),
  };
}
